import useClientVisitOptions from "../../features/clientVisits/useClientVisitOptions";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NAV_CONFIG } from "./navConfig";
import { usePendingAcknowledgements } from "../../features/assets/usePendingAcknowledgements";
import { usePendingLeaveCount } from "../../features/leave/usePendingLeaveCount";
import { usePerformanceNav } from "../../features/performance/usePerformanceQueries";
import { getEmployeeId } from "../../utils/session";
import { cx } from "../../utils/classNames";
import styles from "./Sidebar.module.css";

const Sidebar = ({ isCollapsed, onToggleCollapse }) => {
  const { pathname } = useLocation();
  const [openGroups, setOpenGroups] = useState({});
  const [closedGroups, setClosedGroups] = useState({});

  const performance = usePerformanceNav();
  const clientVisitOptions = useClientVisitOptions();

  // Flags referenced by `showWhen` in navConfig.
  const flags = {
    employee: !!getEmployeeId(),
    evaluator: performance.isEvaluator,
    clientVisits: !!clientVisitOptions.data?.canFile,
  };

  // Counts referenced by `badge` in navConfig.
  const badgeCounts = {
    assetsToAcknowledge: usePendingAcknowledgements(),
    pendingLeaves: usePendingLeaveCount(),
    evaluationActions: performance.myActions,
    evaluationsToRate: performance.toRate,
  };

  const isVisible = (entry) => !entry.showWhen || flags[entry.showWhen];
  const getBadge = (entry) => (entry.badge ? badgeCounts[entry.badge] : 0);

  const visibleGroups = NAV_CONFIG.groups
    .filter(isVisible)
    .map((group) => ({ ...group, items: group.items.filter(isVisible) }))
    .filter((group) => group.items.length > 0);

  // When the route changes, forget "closed by user" for groups the new
  // route doesn't belong to (render-time state adjustment, no effect).
  const [prevPathname, setPrevPathname] = useState(pathname);

  if (pathname !== prevPathname) {
    setPrevPathname(pathname);

    setClosedGroups((prev) => {
      const next = { ...prev };
      let changed = false;

      visibleGroups.forEach((group) => {
        const inGroup = group.items.some((item) => item.path === pathname);
        if (!inGroup && next[group.key]) {
          next[group.key] = false;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }

  const isGroupOpen = (group) => {
    const containsActive = group.items.some((item) => item.path === pathname);
    return (
      !closedGroups[group.key] && (openGroups[group.key] || containsActive)
    );
  };

  const toggleGroup = (group) => {
    if (isCollapsed) return;
    const willOpen = !isGroupOpen(group);
    setOpenGroups((prev) => ({ ...prev, [group.key]: willOpen }));
    setClosedGroups((prev) => ({ ...prev, [group.key]: !willOpen }));
  };

  return (
    <div className={cx(styles.sidebar, isCollapsed && styles.collapsed)}>
      <div className={styles.header}>
        {!isCollapsed && (
          <span className={styles.heading}>Main Navigation</span>
        )}

        <button
          type="button"
          className={`btn p-1 d-flex align-items-center justify-content-center ${styles.collapseButton}`}
          onClick={onToggleCollapse}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <i className={`bi bi-chevron-${isCollapsed ? "right" : "left"}`}></i>
        </button>
      </div>

      <nav className={styles.nav}>
        {NAV_CONFIG.main.filter(isVisible).map((item) => {
          const count = getBadge(item);

          return (
            <Link
              key={item.path}
              to={item.path}
              className={cx(
                styles.link,
                pathname === item.path && styles.linkActive,
              )}
              title={isCollapsed ? item.label : undefined}
            >
              <span className={styles.iconWrap}>
                <i className={`bi ${item.icon}`}></i>
                {isCollapsed && count > 0 && (
                  <span className={styles.badgeDot} />
                )}
              </span>
              {!isCollapsed && (
                <span className={styles.linkLabel}>{item.label}</span>
              )}
              {!isCollapsed && count > 0 && (
                <span className={styles.badge}>{count}</span>
              )}
            </Link>
          );
        })}

        {visibleGroups.map((group) => {
          const open = isGroupOpen(group);
          const groupCount = group.items.reduce(
            (sum, item) => sum + getBadge(item),
            0,
          );

          return (
            <div key={group.key} className={styles.group}>
              <button
                type="button"
                className={cx(styles.link, styles.groupHeader)}
                onClick={() => toggleGroup(group)}
              >
                <span className={styles.groupLabel}>
                  <span className={styles.iconWrap}>
                    <i className={`bi ${group.icon}`}></i>
                    {isCollapsed && groupCount > 0 && (
                      <span className={styles.badgeDot} />
                    )}
                  </span>
                  {!isCollapsed && (
                    <span className={styles.linkLabel}>{group.label}</span>
                  )}
                  {!isCollapsed && !open && groupCount > 0 && (
                    <span className={styles.badge}>{groupCount}</span>
                  )}
                </span>
                {!isCollapsed && (
                  <i
                    className={`bi bi-chevron-${open ? "up" : "down"} ${styles.chevron}`}
                  ></i>
                )}
              </button>

              {open && !isCollapsed && (
                <div className={styles.subLinks}>
                  {group.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cx(
                        styles.link,
                        styles.subLink,
                        pathname === item.path && styles.subLinkActive,
                      )}
                    >
                      <span className={styles.linkLabel}>{item.label}</span>
                      {getBadge(item) > 0 && (
                        <span className={styles.badge}>{getBadge(item)}</span>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default Sidebar;
